import React, { useEffect, useState, useRef } from "react";
import { notification } from "antd";
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { addeditdata } from "../../../controllers/cms/advisory";
import { fetchIpAddress } from "../../../controllers/API";
import LoadingEffect from "../../../components/Loading/LoadingEffect";
import ImageUploadCrop from "../../../components/ImageUploadCrop/ImageUploadCrop";
import { S3_BASE_URL } from "../../../config/constants";

const AdvisoryAddEditPage = () => {
  const targetRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const [error, setError] = useState({});
  const [ADDEDITDATA, setAddEditData] = useState(state ? state : {});
  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /*********************************************************
   *  This function is used to handle input change
   *********************************************************/
  const handleChange = (e) => {
    setAddEditData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError((prev) => ({
      ...prev,
      [e.target.name]: "",
    }));
  };

  const handleAboutChange = (value) => {
    setAddEditData((prev) => ({ ...prev, about: value }));
    setError((prev) => ({ ...prev, about: "" }));
  };

  const handleImageChange = (file) => {
    setImageFile(file);
    setError((prev) => ({ ...prev, image: "" }));
  };

  /*********************************************************
   *  This function is used to handle form submission
   *********************************************************/
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const { _id, name, designation, about } = ADDEDITDATA;
      const ipAddress = (await fetchIpAddress()) || "0.0.0.0";

      const form = new FormData();
      if (_id && _id !== "undefined" && _id !== null) {
        form.append("editId", _id);
      }
      form.append("name", name || "");
      form.append("designation", designation || "");
      form.append("about", about || "");
      if (imageFile) {
        form.append("image", imageFile);
      }
      form.append("ipAddress", ipAddress);

      const isValidate = validateFormData(form);
      if (isValidate) {
        const options = {
          url: `cms/advisory/addeditdata/${_id ? _id : ":editId"}`,
          postData: form,
        };
        const res = await addeditdata(options);

        if (res.status === true) {
          notification.success({
            message: "Data added.",
            description: `Data added successfully.`,
            placement: "topRight",
            icon: <CheckCircleOutlined style={{ color: "green" }} />,
            duration: 2,
          });
          navigate("/cms/advisory");
        } else {
          notification.error({
            message: "Oops!",
            description: `${res?.message}`,
            placement: "topRight",
            icon: <InfoCircleOutlined style={{ color: "red" }} />,
            duration: 2,
          });
        }
      } else {
        notification.error({
          message: "Oops!",
          description: `Please enter the required fields.`,
          placement: "topRight",
          icon: <InfoCircleOutlined style={{ color: "red" }} />,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /*********************************************************
   *  This function is used to validate form data
   *********************************************************/
  const validateFormData = (form) => {
    const newErrors = {};
    if (!ADDEDITDATA?._id && !imageFile) {
      newErrors.image = "Image is required.";
    }
    if (!form.get("name")?.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!form.get("designation")?.trim()) {
      newErrors.designation = "Designation is required.";
    }
    if (Object.keys(newErrors).length > 0) {
      setError((prev) => ({ ...prev, ...newErrors }));
      return false;
    }
    return true;
  };

  /*********************************************************
   *  Scroll to the top and set document title
   *********************************************************/
  useEffect(() => {
    targetRef.current.scrollIntoView({ behavior: "smooth" });
    document.title = `KC High Super Admin || ${ADDEDITDATA?._id ? "Edit" : "Add"} Advisory Board Member`;
  }, [ADDEDITDATA]);

  const existingImageUrl = ADDEDITDATA?.image
    ? `${S3_BASE_URL}${ADDEDITDATA.image}`
    : null;

  return (
    <div ref={targetRef}>
      <div className="container-fluid">
        <div className="Inventory_edit_page_main_div">
          <div>
            <h5 className="inventory_edit_page_heading">
              {ADDEDITDATA?._id ? `Edit` : `Add`} Advisory Board Member
            </h5>
          </div>
          <Link to="/cms/advisory">
            <div>
              <button type="button" className="btn btn-secondary">
                <ArrowLeftOutlined /> Back
              </button>
            </div>
          </Link>
        </div>

        <div className="card shadow mb-4 dataBase_table_Add_Data">
          <div className="card-body">
            <div className="responsive">
              <form method="post" className="inventory_form_main_contnet">
                <div className="form-group">
                  <label className="all_section_for_label_main_class">
                    {!ADDEDITDATA?._id && <span style={{ color: "red" }}>*</span>}
                    Photo
                  </label>
                  <div>
                    <ImageUploadCrop
                      value={existingImageUrl}
                      onChange={handleImageChange}
                      aspectRatio={4 / 5}
                      label="Photo"
                    />
                  </div>
                  <p style={{ color: "blue" }} className="all_top_and_bottom_for_input_text">
                    *Note: Photo is cropped to a 4:5 ratio to match how it
                    displays on the website.
                  </p>
                  {error.image && <p style={{ color: "red" }}>{error.image}</p>}
                </div>

                <div className="form-group">
                  <label className="all_section_for_label_main_class">
                    <span style={{ color: "red" }}>*</span>Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={ADDEDITDATA?.name || ""}
                    onChange={handleChange}
                  />
                  {error.name && <p style={{ color: "red" }}>{error.name}</p>}
                </div>

                <div className="form-group">
                  <label className="all_section_for_label_main_class">
                    <span style={{ color: "red" }}>*</span>Designation
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="designation"
                    value={ADDEDITDATA?.designation || ""}
                    onChange={handleChange}
                  />
                  {error.designation && <p style={{ color: "red" }}>{error.designation}</p>}
                </div>

                <div className="form-group">
                  <label className="all_section_for_label_main_class">
                    About
                  </label>
                  <div className="term_and_condation_react_quilllll">
                    <ReactQuill
                      theme="snow"
                      value={ADDEDITDATA?.about || ""}
                      onChange={handleAboutChange}
                    />
                  </div>
                  {error.about && <p style={{ color: "red" }}>{error.about}</p>}
                </div>

                <div className="inventory_edit_submit_btn">
                  <button
                    type="button"
                    className="btn btn-primary inventory_save_button"
                    onClick={handleSubmit}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <LoadingEffect isLoading={isLoading} />
    </div>
  );
};

export default AdvisoryAddEditPage;
